// Njia Safaris — 42 African itineraries.
// Category is derived from duration: 4–7 days = "families", 8+ days = "professionals".
// No pricing anywhere by design.

const img = (id, w = 2400) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;

const HERO = {
  amboseliSunset: img("1516426122078-c23e76319801"),
  savannahMigration: img("1523805009345-7448845a9e53"),
  savannahLions: img("1547471080-7cc2caa01a7e"),
  elephantWalk: img("1535941339077-2dd1c7963098"),
  zebraPortrait: img("1526095179574-86e545346ae6"),
  lionCubs: img("1504173010664-32509aeebb62"),
  giraffeClose: img("1547721064-da6cfb341d50"),
  lionInGrass: img("1546182990-dffeafbe841d"),
  lionCheetah: img("1552410260-0fd9b577afa6"),
  tropicalBeach: img("1519046904884-53103b34b206"),
  landscapeA: img("1516026672322-bc52d61a55d5"),
  landscapeB: img("1580060839134-75a5edca2e99"),
  landscapeC: img("1502786129293-79981df4e689"),
  landscapeD: img("1541417904950-b855846fe074"),
  landscapeE: img("1596394516093-501ba68a0ba6"),
  landscapeF: img("1600298881974-6be191ceeda1"),
  gorilla: "https://upload.wikimedia.org/wikipedia/commons/a/a0/Susa_group%2C_mountain_gorilla.jpg",
};

// Helper to make itinerary object more compact
const j = (o) => ({
  seoTitle: `${o.name} | Njia Safaris — Ultra-Luxury African Safaris`,
  seoDescription: o.intro,
  thumb: o.hero,
  ...o,
});

export const itineraries = [
  // ================= EXISTING 10 (all 8+ days = professionals) =================
  j({
    slug: "iconic-kenya-safari",
    name: "Iconic Kenya Safari",
    country: "Kenya",
    days: 10,
    duration: "10 days",
    tagline: "Kilimanjaro's shadow, Laikipia's stillness, the Mara's roar.",
    level: "Classic luxury · First-time safari",
    hero: HERO.savannahLions,
    intro:
      "Ten unhurried days along Kenya's most storied plains — where elephant herds cross the salt pans beneath Kilimanjaro, and the Mara turns golden by five.",
    route: ["Nairobi", "Amboseli", "Laikipia", "Masai Mara"],
    highlights: [
      "Amboseli elephants with Kilimanjaro backdrop",
      "Laikipia conservancy — horseback & walking safaris",
      "Great Migration in the Mara",
      "Private guide throughout the journey",
    ],
    lodges: [
      { name: "Tortilis Camp", region: "Amboseli" },
      { name: "Ol Pejeta Bush Camp", region: "Laikipia" },
      { name: "Angama Mara", region: "Masai Mara" },
    ],
    dayList: [
      "Nairobi — arrival, evening at Hemingways.",
      "Fly to Amboseli — Tortilis Camp; Kilimanjaro reveals at dusk.",
      "Amboseli — day among the elephants at the water pans.",
      "Amboseli — sunrise drive, meeting with a Maasai elder.",
      "Fly to Laikipia — Ol Pejeta; rhino tracking on foot.",
      "Laikipia — horseback safari across the plateau.",
      "Fly to Masai Mara — Angama Mara; escarpment reveal.",
      "Masai Mara — river crossing viewing in season.",
      "Masai Mara — dawn balloon, bush breakfast, cat tracking.",
      "Return to Nairobi — evening departure.",
    ],
  }),
  j({
    slug: "a-passage-to-kenya",
    name: "A Passage to Kenya",
    country: "Kenya",
    days: 12,
    duration: "12 days",
    tagline: "From the crimson plains of Samburu to the white sand of Diani.",
    level: "Comprehensive Kenya immersion",
    hero: HERO.elephantWalk,
    intro:
      "A full passage across Kenya — from the ochre north where reticulated giraffe still walk untouched land, to a coast where dhows drift home at dusk.",
    route: ["Nairobi", "Samburu", "Ol Pejeta", "Lake Nakuru", "Masai Mara", "Diani Beach"],
    highlights: [
      "Samburu Special Five",
      "Rhino tracking at Ol Pejeta",
      "Flamingo shores of Lake Nakuru",
      "Big Five in the Masai Mara",
      "Private villa on the Indian Ocean",
    ],
    lodges: [
      { name: "Saruni Samburu", region: "Samburu" },
      { name: "Sweetwaters Tented Camp", region: "Ol Pejeta" },
      { name: "Governors' Camp", region: "Masai Mara" },
      { name: "Alfajiri Villas", region: "Diani Beach" },
    ],
    dayList: [
      "Nairobi — arrival & Giraffe Manor if season allows.",
      "Fly to Samburu — Saruni Samburu; drive along the Ewaso Ng'iro.",
      "Samburu — Special Five search.",
      "Fly to Ol Pejeta — meet the last northern white rhinos.",
      "Ol Pejeta → Lake Nakuru — road transfer, flamingo shores.",
      "Fly to Masai Mara — Governors' Camp.",
      "Masai Mara — full day, predator focus with private vehicle.",
      "Masai Mara — sunrise balloon; Maasai homestead visit.",
      "Fly to Diani — Alfajiri Villas; sunset dhow cruise.",
      "Diani — reef snorkel; long lunch on the sand.",
      "Diani — a day of nothing.",
      "Transfer to Mombasa, connect to Nairobi, depart.",
    ],
  }),
  j({
    slug: "the-regenerative-safari",
    name: "The Regenerative Safari",
    country: "Kenya",
    days: 14,
    duration: "14 days",
    tagline: "The 2026 Signature Journey — travel that returns more than it takes.",
    level: "Purpose-driven ultra-luxury · The future of safari travel",
    hero: HERO.savannahMigration,
    signature: true,
    intro:
      "The 2026 Signature Journey. Fourteen days across Kenya's most storied conservancies, walking beside the people who protect them — a carbon-positive safari designed to return more than it takes.",
    route: ["Nairobi", "Lewa", "Borana", "Northern Rangelands", "Masai Mara", "Amboseli"],
    highlights: [
      "Community-led conservation with local rangers",
      "Wildlife monitoring alongside researchers",
      "Indigenous tree planting on private land",
      "Anti-poaching patrol experience",
      "Cultural exchange with Maasai & Samburu",
      "Carbon-positive itinerary — verified offset",
    ],
    lodges: [
      { name: "Lewa Safari Camp", region: "Lewa Wildlife Conservancy" },
      { name: "Borana Lodge", region: "Borana Conservancy" },
      { name: "Saruni Rhino", region: "Sera Community Conservancy" },
      { name: "Sala's Camp", region: "Masai Mara" },
    ],
    dayList: [
      "Nairobi — private briefing on regenerative travel; Hemingways.",
      "Fly to Lewa — Lewa Safari Camp; afternoon with conservation team.",
      "Lewa — ranger patrol at dawn; rhino monitoring with researchers.",
      "Lewa → Borana — cross-conservancy transfer; tracking on foot.",
      "Borana — community visit; schools funded through conservation.",
      "Borana — indigenous tree planting; sundowners at a private kopje.",
      "Fly to Sera — Saruni Rhino; camel-supported walking safari.",
      "Sera — rhino tracking on foot with Samburu warriors.",
      "Sera — a day with Samburu elders.",
      "Fly to Masai Mara — Sala's Camp; along the Sand River.",
      "Masai Mara — with Mara research team; predator methodology.",
      "Masai Mara → Amboseli — private transfer; Tortilis.",
      "Amboseli — elephant research; matriarch monitoring session.",
      "Return — farewell breakfast, flight to Nairobi, departure.",
    ],
  }),
  j({
    slug: "absolute-tanzania",
    name: "Absolute Tanzania",
    country: "Tanzania",
    days: 10,
    duration: "10 days",
    tagline: "Baobab plains, the crater floor, and the endless Serengeti.",
    level: "Premium Tanzania circuit",
    hero: HERO.amboseliSunset,
    intro:
      "Tanzania at its most complete. Ten days across the great circuit — baobab-studded Tarangire, the crater floor at Ngorongoro, and the migration in the Serengeti.",
    route: ["Arusha", "Tarangire", "Ngorongoro Crater", "Central Serengeti", "Northern Serengeti"],
    highlights: [
      "Baobab-studded Tarangire",
      "Ngorongoro Crater floor game drive",
      "Serengeti river crossings",
      "Hot air balloon safari at dawn",
    ],
    lodges: [
      { name: "Chem Chem Lodge", region: "Tarangire" },
      { name: "The Highlands", region: "Ngorongoro" },
      { name: "Singita Faru Faru", region: "Central Serengeti" },
      { name: "Lamai Serengeti", region: "Northern Serengeti" },
    ],
    dayList: [
      "Arusha — arrival, overnight at Legendary Lodge.",
      "Fly to Tarangire — Chem Chem; drive through baobab country.",
      "Tarangire — elephant herds along the river; dawn walking safari.",
      "Transfer to Ngorongoro — The Highlands; afternoon at the crater rim.",
      "Ngorongoro Crater — full-day descent to the crater floor.",
      "Fly to Central Serengeti — Singita Faru Faru.",
      "Central Serengeti — dawn balloon; day with big cats.",
      "Fly to Northern Serengeti — Lamai; river crossings in season.",
      "Northern Serengeti — picnic on a rocky outcrop.",
      "Return to Arusha — flight back and evening departure.",
    ],
  }),
  j({
    slug: "tanzania-private-wilderness",
    name: "Tanzania Private Wilderness",
    country: "Tanzania",
    days: 12,
    duration: "12 days",
    tagline: "The parks few reach — Ruaha, Katavi, Mahale, and Zanzibar to close.",
    level: "Exclusive · Remote · For repeat safari-goers",
    hero: HERO.lionCubs,
    intro:
      "Twelve days off the mapped safari lines — into Ruaha's wild dogs, Katavi's floodplains, chimpanzee dawns at Mahale, closing in Zanzibar's spice-scented alleys.",
    route: ["Arusha", "Ruaha", "Katavi", "Mahale Mountains", "Zanzibar"],
    highlights: [
      "Wild dogs and big prides in Ruaha",
      "Untouched Katavi floodplains",
      "Chimpanzee trekking at Mahale",
      "Stone Town heritage & Zanzibar coast",
    ],
    lodges: [
      { name: "Jongomero", region: "Ruaha" },
      { name: "Chada Katavi", region: "Katavi" },
      { name: "Greystoke Mahale", region: "Mahale" },
      { name: "The Residence Zanzibar", region: "Zanzibar" },
    ],
    dayList: [
      "Arusha — arrival and overnight.",
      "Fly to Ruaha — Jongomero; afternoon drive.",
      "Ruaha — full day; wild dog searching.",
      "Ruaha — walking safari; night drive.",
      "Fly to Katavi — Chada Katavi; afternoon on the floodplains.",
      "Katavi — hippo pods; big buffalo herds.",
      "Fly to Mahale — Greystoke; boat arrival across Tanganyika.",
      "Mahale — chimpanzee trek at dawn.",
      "Mahale — second trek or forest walk; lake dhow at sunset.",
      "Fly to Zanzibar — The Residence; Stone Town wander.",
      "Zanzibar — reef, spice tour, long lunch.",
      "Depart — Zanzibar to Dar and home.",
    ],
  }),
  j({
    slug: "pure-tanzania",
    name: "Pure Tanzania",
    country: "Tanzania",
    days: 8,
    duration: "8 days",
    tagline: "The essential Tanzania — shorter, sharper, no less luxurious.",
    level: "Essential Tanzania · Shorter luxury itinerary",
    hero: HERO.zebraPortrait,
    intro:
      "Eight days of Tanzania distilled — tree-climbing lions of Manyara, the crater's silent floor, and the endless Serengeti.",
    route: ["Arusha", "Lake Manyara", "Ngorongoro", "Serengeti"],
    highlights: [
      "Tree-climbing lions of Manyara",
      "Ngorongoro Crater floor picnic",
      "Endless Serengeti plains",
      "Big cat tracking with private guide",
    ],
    lodges: [
      { name: "&Beyond Lake Manyara Tree Lodge", region: "Lake Manyara" },
      { name: "&Beyond Ngorongoro Crater Lodge", region: "Ngorongoro" },
      { name: "Four Seasons Serengeti", region: "Serengeti" },
    ],
    dayList: [
      "Arusha — arrival.",
      "Manyara — Tree Lodge; afternoon drive.",
      "Manyara → Ngorongoro — transfer; sundowners on the rim.",
      "Ngorongoro Crater — full-day descent.",
      "Fly to Serengeti — Four Seasons; afternoon drive.",
      "Serengeti — dawn balloon; big cats.",
      "Serengeti — picnic on a kopje.",
      "Return — fly to Arusha and depart.",
    ],
  }),
  j({
    slug: "kenya-diaries",
    name: "Kenya Diaries",
    country: "Kenya",
    days: 9,
    duration: "9 days",
    tagline: "An intimate wildlife diary — Samburu, Laikipia, and the Mara.",
    level: "Intimate · Diary-style journey",
    hero: HERO.lionInGrass,
    intro:
      "Nine days written like a diary — reticulated giraffe in the north, night drives on Laikipia, and river crossings on the Mara.",
    route: ["Nairobi", "Samburu", "Laikipia", "Masai Mara"],
    highlights: [
      "Reticulated giraffe in Samburu",
      "Night game drives in Laikipia",
      "Wildebeest river crossings in season",
      "Sundowners on the Mara",
    ],
    lodges: [
      { name: "Elephant Bedroom Camp", region: "Samburu" },
      { name: "Segera Retreat", region: "Laikipia" },
      { name: "Mara Plains Camp", region: "Masai Mara" },
    ],
    dayList: [
      "Nairobi — arrival.",
      "Fly to Samburu — Elephant Bedroom.",
      "Samburu — Special Five search.",
      "Fly to Laikipia — Segera; night drive.",
      "Laikipia — art walk; conservancy time.",
      "Fly to Masai Mara — Mara Plains.",
      "Masai Mara — big cat tracking.",
      "Masai Mara — balloon; sundowners.",
      "Depart — fly to Nairobi and home.",
    ],
  }),
  j({
    slug: "kenya-ndoto-safari",
    name: "Kenya Ndoto Safari",
    country: "Kenya",
    days: 11,
    duration: "11 days",
    tagline: "The northern frontier — camels, forests, and a Maasai homeland.",
    level: "Adventure luxury · Off-grid",
    hero: HERO.giraffeClose,
    intro:
      "Ndoto means 'dream'. Eleven days into a remote north few reach — camel-supported walking safaris, cycad forests, and the endless Mara to close.",
    route: ["Nairobi", "Matthews Range", "Samburu", "Lewa", "Masai Mara"],
    highlights: [
      "Camel-supported walking safari",
      "Endangered species tracking",
      "Northern frontier landscapes",
      "Mara predator viewing",
    ],
    lodges: [
      { name: "Kitich Forest Camp", region: "Matthews Range" },
      { name: "Saruni Samburu", region: "Samburu" },
      { name: "Lewa House", region: "Lewa" },
      { name: "Cottar's 1920s Camp", region: "Masai Mara" },
    ],
    dayList: [
      "Nairobi — arrival.",
      "Fly to Matthews Range — Kitich; afternoon walk.",
      "Matthews Range — cycad forest walk with Samburu guides.",
      "Camel-supported transfer — slow overland to Samburu.",
      "Samburu — Saruni Samburu; Special Five.",
      "Fly to Lewa — Lewa House; horseback safari.",
      "Lewa — rhino conservation morning.",
      "Fly to Masai Mara — Cottar's 1920s Camp.",
      "Masai Mara — full day; private vehicle.",
      "Masai Mara — balloon safari; farewell sundowner.",
      "Depart — fly to Nairobi and home.",
    ],
  }),
  j({
    slug: "tanzania-johari-safari",
    name: "Tanzania Johari Safari",
    country: "Tanzania",
    days: 10,
    duration: "10 days",
    tagline: "Bush to beach — Tarangire, Ngorongoro, Serengeti, Zanzibar.",
    level: "Bush-to-beach luxury",
    hero: HERO.tropicalBeach,
    intro:
      "Johari means 'jewel'. Ten days threading Tanzania's jewels — from Tarangire's elephant herds to the spice-scented sand of Zanzibar.",
    route: ["Arusha", "Tarangire", "Ngorongoro", "Serengeti", "Zanzibar"],
    highlights: [
      "Elephant herds of Tarangire",
      "Ngorongoro Crater wildlife",
      "Serengeti sunsets",
      "Zanzibar spice tour and coast",
    ],
    lodges: [
      { name: "Oliver's Camp", region: "Tarangire" },
      { name: "Entamanu Ngorongoro", region: "Ngorongoro" },
      { name: "Nomad Serengeti Safari Camp", region: "Serengeti" },
      { name: "Baraza Resort & Spa", region: "Zanzibar" },
    ],
    dayList: [
      "Arusha — arrival.",
      "Fly to Tarangire — Oliver's Camp.",
      "Tarangire — walking safari; elephant herds.",
      "Ngorongoro — Entamanu; rim views.",
      "Crater day — full-day descent.",
      "Fly to Serengeti — Nomad; afternoon drive.",
      "Serengeti — big cats and picnic day.",
      "Fly to Zanzibar — Baraza; beach afternoon.",
      "Zanzibar — Stone Town; spice tour.",
      "Depart — home.",
    ],
  }),
  j({
    slug: "gorillas-and-the-nile",
    name: "Gorillas and the Nile",
    country: "Uganda",
    days: 10,
    duration: "10 days",
    tagline: "Bwindi's mountain gorillas, Ishasha's tree-climbing lions, the Nile.",
    level: "Primate-focused adventure luxury",
    hero: HERO.gorilla,
    intro:
      "Ten days across Uganda's greenest terrain — an hour with the mountain gorillas, tree-climbing lions of Ishasha, and the Nile's thunder at Murchison.",
    route: ["Entebbe", "Bwindi Impenetrable Forest", "Queen Elizabeth NP", "Murchison Falls", "Jinja"],
    highlights: [
      "Mountain gorilla trekking",
      "Tree-climbing lions of Ishasha",
      "Murchison Falls boat safari",
      "White-water rafting on the Nile",
    ],
    lodges: [
      { name: "Clouds Mountain Gorilla Lodge", region: "Bwindi" },
      { name: "Kyambura Gorge Lodge", region: "Queen Elizabeth NP" },
      { name: "Paraa Safari Lodge", region: "Murchison Falls" },
      { name: "Wildwaters Lodge", region: "Jinja" },
    ],
    dayList: [
      "Entebbe — arrival.",
      "Fly to Bwindi — Clouds Mountain Gorilla Lodge.",
      "Gorilla trek — one hour with the family.",
      "Second trek option — or forest walk with Batwa guides.",
      "Transfer to Queen Elizabeth NP — Kyambura; afternoon drive.",
      "Ishasha — tree-climbing lions.",
      "Fly to Murchison Falls — Paraa; boat to the falls.",
      "Murchison — big herds and Rothschild giraffe.",
      "Fly to Jinja — Wildwaters Lodge, an island in the Nile.",
      "Depart — return to Entebbe and home.",
    ],
  }),

  // ================= NEW: FAMILIES & TRAVEL ENTHUSIASTS (4–7 days) =================
  j({
    slug: "amani-safari",
    name: "Amani Safari",
    country: "Kenya",
    days: 5,
    duration: "5 days",
    tagline: "Peace, at the foot of Kilimanjaro.",
    level: "Family & enthusiasts · Short-form luxury",
    hero: HERO.amboseliSunset,
    intro:
      "Amani means 'peace'. Five slow days beneath Kilimanjaro — elephant herds at the salt pans, sundowners on the ridge, and long, undisturbed afternoons.",
    route: ["Nairobi", "Amboseli"],
    highlights: [
      "Amboseli elephant families",
      "Kilimanjaro views at dawn",
      "Maasai cultural walk",
      "Private guide throughout",
    ],
    lodges: [
      { name: "Tortilis Camp", region: "Amboseli" },
      { name: "Hemingways Nairobi", region: "Nairobi" },
    ],
    dayList: [
      "Nairobi — arrival at Hemingways.",
      "Fly to Amboseli — Tortilis Camp; afternoon drive.",
      "Amboseli — full day with the elephants.",
      "Amboseli — sunrise drive; cultural visit.",
      "Return to Nairobi — depart.",
    ],
  }),
  j({
    slug: "masai-country-safari",
    name: "Masai Country Safari",
    country: "Kenya",
    days: 6,
    duration: "6 days",
    tagline: "Six days in the heart of Maasai land.",
    level: "Family & enthusiasts · Cultural safari",
    hero: HERO.lionInGrass,
    intro:
      "Six days threading the great plains of Maasai country — respectful cultural exchange, big cat drives, and open savannah horizons.",
    route: ["Nairobi", "Masai Mara"],
    highlights: [
      "Big cats of the Mara",
      "Maasai homestead visit",
      "Sundowners on the plains",
      "Balloon safari (optional add-on)",
    ],
    lodges: [
      { name: "Governors' Camp", region: "Masai Mara" },
    ],
    dayList: [
      "Nairobi — arrival.",
      "Fly to Masai Mara — Governors' Camp; afternoon drive.",
      "Mara — full-day predator focus.",
      "Mara — river crossings in season; sundowners.",
      "Mara — cultural exchange with a Maasai family.",
      "Return — fly to Nairobi and depart.",
    ],
  }),
  j({
    slug: "masai-mara-express-4-star",
    name: "Masai Mara Express — 4★",
    country: "Kenya",
    days: 4,
    duration: "4 days",
    tagline: "The Mara, essential — a fast, four-day tented safari.",
    level: "Family & enthusiasts · Weekend safari",
    hero: HERO.lionCubs,
    intro:
      "Four days in the Masai Mara at a warm, well-run four-star tented camp — dawn game drives, big-cat focus, senior guide.",
    route: ["Nairobi", "Masai Mara"],
    highlights: [
      "Big Five in the Mara",
      "Shared 4x4 with expert guide",
      "Sundowners on the plains",
      "Bush breakfast",
    ],
    lodges: [
      { name: "Mara Serena Safari Lodge", region: "Masai Mara" },
    ],
    dayList: [
      "Nairobi — arrival, connecting flight to Mara.",
      "Mara — first afternoon drive.",
      "Mara — dawn to dusk with experienced guide.",
      "Return — morning drive, fly to Nairobi, depart.",
    ],
  }),
  j({
    slug: "masai-mara-express-5-star",
    name: "Masai Mara Express — 5★",
    country: "Kenya",
    days: 4,
    duration: "4 days",
    tagline: "The Mara, five-star — a private 4x4, a signature camp, four days.",
    level: "Family & enthusiasts · Weekend safari",
    hero: HERO.lionInGrass,
    intro:
      "Four days at one of the Mara's signature camps — a private 4x4 with a senior Njia guide throughout, and no shared vehicles.",
    route: ["Nairobi", "Masai Mara"],
    highlights: [
      "Big Five in the Mara",
      "Private 4x4 with senior guide",
      "Signature five-star camp",
      "Bush breakfast & sundowners",
    ],
    lodges: [
      { name: "Mara Plains Camp", region: "Masai Mara" },
    ],
    dayList: [
      "Nairobi — arrival, connecting flight to Mara.",
      "Mara — first afternoon drive with private vehicle.",
      "Mara — dawn to dusk with a senior guide.",
      "Return — morning drive, fly to Nairobi, depart.",
    ],
  }),
  j({
    slug: "nairobi-magic",
    name: "Nairobi Magic",
    country: "Kenya",
    days: 4,
    duration: "4 days",
    tagline: "The city and the wild — four days, no compromise.",
    level: "Family & enthusiasts · City + safari",
    hero: HERO.giraffeClose,
    intro:
      "A four-day introduction to Kenya — Giraffe Manor, the Elephant Orphanage, Nairobi National Park at dawn, and a slow evening at Karen Blixen's.",
    route: ["Nairobi", "Nairobi National Park"],
    highlights: [
      "Breakfast with Rothschild giraffe",
      "David Sheldrick Elephant Orphanage",
      "Nairobi National Park game drive",
      "Karen Blixen Museum",
    ],
    lodges: [
      { name: "Giraffe Manor", region: "Nairobi" },
      { name: "Hemingways Nairobi", region: "Nairobi" },
    ],
    dayList: [
      "Arrival — Giraffe Manor check-in.",
      "Nairobi National Park at dawn; elephant orphanage after.",
      "Karen Blixen Museum; Kazuri beads; long lunch at Talisman.",
      "Depart — morning at leisure, evening flight.",
    ],
  }),
  j({
    slug: "garden-of-eden",
    name: "Garden of Eden",
    country: "Kenya",
    days: 7,
    duration: "7 days",
    tagline: "A quiet paradise — the Rift Valley and the Mara.",
    level: "Family & enthusiasts · Landscape-led safari",
    hero: HERO.landscapeA,
    intro:
      "Seven days across Kenya's most cinematic landscapes — Great Rift Valley lakes, alkaline flats, and closing on the Mara.",
    route: ["Nairobi", "Lake Naivasha", "Lake Nakuru", "Masai Mara"],
    highlights: [
      "Boat safari on Lake Naivasha",
      "Flamingos of Lake Nakuru",
      "Big cats of the Mara",
      "Rift escarpment views",
    ],
    lodges: [
      { name: "Loldia House", region: "Lake Naivasha" },
      { name: "The Cliff", region: "Lake Nakuru" },
      { name: "Governors' Camp", region: "Masai Mara" },
    ],
    dayList: [
      "Nairobi — arrival.",
      "Naivasha — Loldia House; boat safari at dusk.",
      "Nakuru — flamingo shores; rhino spotting.",
      "Fly to Mara — Governors'; afternoon drive.",
      "Mara — dawn to dusk, predator focus.",
      "Mara — cultural exchange; sundowners.",
      "Return — fly to Nairobi and depart.",
    ],
  }),
  j({
    slug: "maisha-mzuri-safari",
    name: "Maisha Mzuri Safari",
    country: "Kenya",
    days: 7,
    duration: "7 days",
    tagline: "'The good life' — a well-composed Kenyan week.",
    level: "Family & enthusiasts · Balanced luxury",
    hero: HERO.landscapeB,
    intro:
      "Maisha Mzuri means 'the good life'. Seven days of unhurried luxury — a park, a plateau, and the Mara to close.",
    route: ["Nairobi", "Amboseli", "Masai Mara"],
    highlights: [
      "Amboseli elephants at Kilimanjaro",
      "Big cats of the Mara",
      "Sundowners on the plains",
      "Slow, private pace",
    ],
    lodges: [
      { name: "Tortilis Camp", region: "Amboseli" },
      { name: "Sala's Camp", region: "Masai Mara" },
    ],
    dayList: [
      "Nairobi — arrival at Hemingways.",
      "Fly to Amboseli — Tortilis Camp.",
      "Amboseli — day with the elephants.",
      "Fly to Masai Mara — Sala's Camp.",
      "Mara — full day with private guide.",
      "Mara — balloon and bush breakfast.",
      "Return to Nairobi — depart.",
    ],
  }),
  j({
    slug: "tanzania-eden",
    name: "Tanzania Eden",
    country: "Tanzania",
    days: 7,
    duration: "7 days",
    tagline: "The essential Tanzanian week — crater, plains, and quiet camps.",
    level: "Family & enthusiasts · Short Tanzania circuit",
    hero: HERO.landscapeC,
    intro:
      "Seven days of Tanzania's most iconic wildlife country — the Ngorongoro Crater and the Serengeti's endless plains.",
    route: ["Arusha", "Ngorongoro", "Serengeti"],
    highlights: [
      "Ngorongoro Crater floor",
      "Serengeti big cats",
      "Bush breakfast on the plains",
      "Kopje sundowners",
    ],
    lodges: [
      { name: "The Highlands", region: "Ngorongoro" },
      { name: "Nomad Serengeti Safari Camp", region: "Serengeti" },
    ],
    dayList: [
      "Arusha — arrival at Legendary Lodge.",
      "Transfer to Ngorongoro — The Highlands; rim views.",
      "Ngorongoro Crater — full-day descent.",
      "Fly to Serengeti — Nomad; afternoon drive.",
      "Serengeti — full day, big cats.",
      "Serengeti — sunrise drive; kopje lunch.",
      "Return — fly to Arusha and depart.",
    ],
  }),
  j({
    slug: "tanzania-under-canvas",
    name: "Tanzania Under Canvas",
    country: "Tanzania",
    days: 7,
    duration: "7 days",
    tagline: "Seven nights in tented camps — the old-fashioned safari, refined.",
    level: "Family & enthusiasts · Tented luxury",
    hero: HERO.landscapeD,
    intro:
      "A seven-day tented journey — no walls, no hurry. Serengeti mobile camps, canvas ceilings, lantern light.",
    route: ["Arusha", "Tarangire", "Serengeti"],
    highlights: [
      "Mobile tented camps that move with the migration",
      "Baobab country of Tarangire",
      "Serengeti at close range",
      "Long, unhurried mealtimes",
    ],
    lodges: [
      { name: "Oliver's Camp", region: "Tarangire" },
      { name: "Serengeti Under Canvas", region: "Serengeti" },
    ],
    dayList: [
      "Arusha — arrival.",
      "Fly to Tarangire — Oliver's Camp.",
      "Tarangire — walking and driving safaris.",
      "Fly to Serengeti — Under Canvas.",
      "Serengeti — dawn drive; big cat focus.",
      "Serengeti — bush breakfast; afternoon walk.",
      "Return — fly to Arusha and depart.",
    ],
  }),
  j({
    slug: "tanzania-serengeti-express",
    name: "Tanzania Serengeti Express",
    country: "Tanzania",
    days: 5,
    duration: "5 days",
    tagline: "The Serengeti, five days — for guests who know exactly what they want.",
    level: "Family & enthusiasts · Weekend safari",
    hero: HERO.zebraPortrait,
    intro:
      "Five decisive days in the Serengeti — crater rim, plains, big cats. No filler, no compromise.",
    route: ["Arusha", "Ngorongoro", "Serengeti"],
    highlights: [
      "Ngorongoro Crater rim & floor",
      "Central Serengeti big cats",
      "Private 4x4 with senior guide",
      "Sundowners on a kopje",
    ],
    lodges: [
      { name: "The Highlands", region: "Ngorongoro" },
      { name: "Nomad Serengeti Safari Camp", region: "Serengeti" },
    ],
    dayList: [
      "Arusha — arrival.",
      "Transfer to Ngorongoro — Highlands; rim views.",
      "Crater day — full descent.",
      "Fly to Serengeti — Nomad; afternoon drive.",
      "Serengeti — morning drive, return to Arusha.",
    ],
  }),
  j({
    slug: "tanzania-tarangire-quick",
    name: "Tanzania Tarangire Quick",
    country: "Tanzania",
    days: 4,
    duration: "4 days",
    tagline: "Elephants and baobabs — a proper, tight four days.",
    level: "Family & enthusiasts · Long weekend",
    hero: HERO.elephantWalk,
    intro:
      "Four days in Tarangire — elephant herds, baobab country, walking safaris. Kilimanjaro views on the flight in.",
    route: ["Arusha", "Tarangire"],
    highlights: [
      "Elephant herds along the Tarangire River",
      "Baobab country walking safari",
      "Sundowners on the ridge",
      "Private 4x4 throughout",
    ],
    lodges: [
      { name: "Chem Chem Lodge", region: "Tarangire" },
    ],
    dayList: [
      "Arusha — arrival, transfer to Chem Chem.",
      "Tarangire — dawn drive; walking safari.",
      "Tarangire — elephant herds along the river.",
      "Return — morning drive, fly to Arusha, depart.",
    ],
  }),
  j({
    slug: "uganda-gorilla-express",
    name: "Uganda Gorilla Express",
    country: "Uganda",
    days: 5,
    duration: "5 days",
    tagline: "One hour with the mountain gorillas — five decisive days.",
    level: "Family & enthusiasts · Focused primate safari",
    hero: HERO.gorilla,
    intro:
      "Five days in Uganda designed around a single, extraordinary hour — mountain gorilla trekking in Bwindi. Fly in, trek, out.",
    route: ["Entebbe", "Bwindi Impenetrable Forest"],
    highlights: [
      "One-hour mountain gorilla trek",
      "Batwa cultural exchange",
      "Bwindi forest walk",
      "Private guide throughout",
    ],
    lodges: [
      { name: "Clouds Mountain Gorilla Lodge", region: "Bwindi" },
    ],
    dayList: [
      "Entebbe — arrival, overnight.",
      "Fly to Bwindi — Clouds Mountain Gorilla Lodge.",
      "Gorilla trek — one hour with the family.",
      "Second trek option or forest walk with Batwa guides.",
      "Return — fly to Entebbe and depart.",
    ],
  }),
  j({
    slug: "kenya-johari-safari",
    name: "Kenya Johari Safari",
    country: "Kenya",
    days: 8,
    duration: "8 days",
    tagline: "The jewels of Kenya — Amboseli, Nakuru, Mara.",
    level: "Photographers & professionals · Classic circuit",
    hero: HERO.amboseliSunset,
    intro:
      "Johari means 'jewel'. Eight days threading Kenya's most photographable landscapes — elephants at the salt pan, flamingos on the flats, and cats on the Mara.",
    route: ["Nairobi", "Amboseli", "Lake Nakuru", "Masai Mara"],
    highlights: [
      "Amboseli elephants with Kilimanjaro",
      "Lake Nakuru flamingos",
      "Big Five in the Mara",
      "Private 4x4 with senior guide",
    ],
    lodges: [
      { name: "Tortilis Camp", region: "Amboseli" },
      { name: "The Cliff", region: "Lake Nakuru" },
      { name: "Angama Mara", region: "Masai Mara" },
    ],
    dayList: [
      "Nairobi — arrival.",
      "Fly to Amboseli — Tortilis; afternoon drive.",
      "Amboseli — full day with elephants.",
      "Fly to Nakuru — The Cliff; flamingo shores.",
      "Nakuru — rhino spotting; escarpment views.",
      "Fly to Masai Mara — Angama.",
      "Mara — full-day predator focus.",
      "Return — fly to Nairobi and depart.",
    ],
  }),
  j({
    slug: "kenya-johari-na-bahari-safari",
    name: "Kenya Johari na Bahari Safari",
    country: "Kenya",
    days: 10,
    duration: "10 days",
    tagline: "Jewels of the bush and the coast — Kenya, edge to edge.",
    level: "Photographers & professionals · Bush and beach",
    hero: HERO.savannahLions,
    intro:
      "Johari na Bahari — 'jewels and ocean'. Ten days threading Kenya from the great plains to the Indian Ocean's coral coast.",
    route: ["Nairobi", "Amboseli", "Masai Mara", "Diani Beach"],
    highlights: [
      "Amboseli elephant families",
      "Big cats of the Mara",
      "Private villa on the Indian Ocean",
      "Reef snorkelling & dhow sailing",
    ],
    lodges: [
      { name: "Tortilis Camp", region: "Amboseli" },
      { name: "Angama Mara", region: "Masai Mara" },
      { name: "Alfajiri Villas", region: "Diani Beach" },
    ],
    dayList: [
      "Nairobi — arrival.",
      "Fly to Amboseli — Tortilis.",
      "Amboseli — full day.",
      "Fly to Masai Mara — Angama.",
      "Mara — dawn to dusk with private guide.",
      "Mara — balloon; sundowners.",
      "Fly to Diani — Alfajiri Villas.",
      "Diani — reef snorkel.",
      "Diani — dhow at sunset.",
      "Depart — Mombasa to Nairobi, home.",
    ],
  }),
  j({
    slug: "kenya-pioneer-safari",
    name: "Kenya Pioneer Safari",
    country: "Kenya",
    days: 9,
    duration: "9 days",
    tagline: "In the footsteps of the great safari pioneers.",
    level: "Photographers & professionals · Heritage safari",
    hero: HERO.savannahMigration,
    intro:
      "Nine days along the old safari routes — Cottar's, Loldia, the Mara — a heritage journey in the footsteps of the great East African pioneers.",
    route: ["Nairobi", "Lake Naivasha", "Laikipia", "Masai Mara"],
    highlights: [
      "Historic camps and lodges",
      "Loldia House heritage",
      "Laikipia horseback safari",
      "Cottar's 1920s Camp",
    ],
    lodges: [
      { name: "Loldia House", region: "Lake Naivasha" },
      { name: "Segera Retreat", region: "Laikipia" },
      { name: "Cottar's 1920s Camp", region: "Masai Mara" },
    ],
    dayList: [
      "Nairobi — arrival.",
      "Naivasha — Loldia House; boat safari.",
      "Naivasha — cycling, sundowners.",
      "Fly to Laikipia — Segera; horseback safari.",
      "Laikipia — art walk; night drive.",
      "Fly to Masai Mara — Cottar's 1920s.",
      "Mara — full-day drive.",
      "Mara — balloon; farewell sundowners.",
      "Depart — fly to Nairobi and home.",
    ],
  }),
  j({
    slug: "kenya-mugumo-tree-safari",
    name: "Kenya Mugumo Tree Safari",
    country: "Kenya",
    days: 8,
    duration: "8 days",
    tagline: "The sacred fig — a Kikuyu-rooted journey through Kenya.",
    level: "Photographers & professionals · Cultural safari",
    hero: HERO.landscapeF,
    intro:
      "The mugumo — the sacred fig — is a sanctuary tree in Kikuyu tradition. Eight days along a route shaped by the trees, the ridges, and the elders who protect them.",
    route: ["Nairobi", "Mount Kenya", "Laikipia", "Masai Mara"],
    highlights: [
      "Mount Kenya forest walks",
      "Kikuyu cultural exchange",
      "Laikipia conservancy day",
      "Mara plains to close",
    ],
    lodges: [
      { name: "Serena Mountain Lodge", region: "Mount Kenya" },
      { name: "Ol Pejeta Bush Camp", region: "Laikipia" },
      { name: "Mara Plains Camp", region: "Masai Mara" },
    ],
    dayList: [
      "Nairobi — arrival.",
      "Mount Kenya — forest walks.",
      "Mount Kenya — cultural exchange with elders.",
      "Transfer to Laikipia — Ol Pejeta.",
      "Laikipia — rhino tracking; conservancy day.",
      "Fly to Masai Mara — Mara Plains.",
      "Mara — full day.",
      "Return — fly to Nairobi and depart.",
    ],
  }),
  j({
    slug: "kenya-tree-where-man-was-born",
    name: "The Tree Where Man Was Born",
    country: "Kenya",
    days: 10,
    duration: "10 days",
    tagline: "A homage to the East Africa of Peter Matthiessen.",
    level: "Photographers & professionals · Literary safari",
    hero: HERO.elephantWalk,
    intro:
      "Ten days that walk the ground of Matthiessen's East Africa — the Mara, the Rift Valley, and the northern frontier. Slow, literary, and lit by lanterns.",
    route: ["Nairobi", "Masai Mara", "Rift Valley", "Samburu"],
    highlights: [
      "Great Rift Valley crossings",
      "Samburu Special Five",
      "Mara predator dawns",
      "Slow, unhurried pace",
    ],
    lodges: [
      { name: "Cottar's 1920s Camp", region: "Masai Mara" },
      { name: "Loldia House", region: "Naivasha" },
      { name: "Saruni Samburu", region: "Samburu" },
    ],
    dayList: [
      "Nairobi — arrival.",
      "Fly to Masai Mara — Cottar's.",
      "Mara — full day.",
      "Mara — river crossings in season.",
      "Fly to Naivasha — Loldia House.",
      "Naivasha — Rift Valley walks.",
      "Fly to Samburu — Saruni Samburu.",
      "Samburu — Special Five.",
      "Samburu — cultural day.",
      "Return — fly to Nairobi and depart.",
    ],
  }),
  j({
    slug: "kumbu-kumbu-safari",
    name: "Kumbu Kumbu Safari",
    country: "Kenya",
    days: 12,
    duration: "12 days",
    tagline: "A memory-keeper's journey through Kenya.",
    level: "Photographers & professionals · Long-form",
    hero: HERO.lionInGrass,
    intro:
      "Kumbu-kumbu means 'to remember'. Twelve days across Kenya composed for guests who want to leave with a full journal — not a highlight reel.",
    route: ["Nairobi", "Samburu", "Laikipia", "Masai Mara", "Amboseli"],
    highlights: [
      "Samburu Special Five",
      "Laikipia conservancy walks",
      "Mara predator focus",
      "Amboseli elephants to close",
    ],
    lodges: [
      { name: "Saruni Samburu", region: "Samburu" },
      { name: "Segera Retreat", region: "Laikipia" },
      { name: "Angama Mara", region: "Masai Mara" },
      { name: "Tortilis Camp", region: "Amboseli" },
    ],
    dayList: [
      "Nairobi — arrival.",
      "Fly to Samburu — Saruni Samburu.",
      "Samburu — Special Five.",
      "Samburu — cultural day.",
      "Fly to Laikipia — Segera.",
      "Laikipia — horseback and night drive.",
      "Fly to Masai Mara — Angama.",
      "Mara — full day with private vehicle.",
      "Mara — balloon; kopje picnic.",
      "Fly to Amboseli — Tortilis.",
      "Amboseli — elephant families.",
      "Return — Nairobi and depart.",
    ],
  }),
  j({
    slug: "life-of-my-choice",
    name: "Life of My Choice",
    country: "Kenya",
    days: 11,
    duration: "11 days",
    tagline: "The Thesiger route — the northern frontier, on foot and by camel.",
    level: "Photographers & professionals · Adventure",
    hero: HERO.giraffeClose,
    intro:
      "A homage to Wilfred Thesiger's East Africa. Eleven days into the northern frontier — camel-supported walks, cycad forests, and the pace of another century.",
    route: ["Nairobi", "Matthews Range", "Samburu", "Chalbi"],
    highlights: [
      "Camel-supported walking safari",
      "Matthews Range cycad forests",
      "Samburu cultural exchange",
      "Chalbi Desert crossing",
    ],
    lodges: [
      { name: "Kitich Forest Camp", region: "Matthews Range" },
      { name: "Sasaab", region: "Samburu" },
      { name: "Desert Rose", region: "Mount Nyiro" },
    ],
    dayList: [
      "Nairobi — arrival.",
      "Fly to Matthews Range — Kitich.",
      "Matthews Range — forest walks.",
      "Camel-supported transfer to Samburu.",
      "Samburu — Sasaab; Special Five.",
      "Samburu — cultural day.",
      "Fly to Mount Nyiro — Desert Rose.",
      "Nyiro — mountain climbs.",
      "Chalbi Desert crossing.",
      "Return to Nairobi.",
      "Depart.",
    ],
  }),
  j({
    slug: "the-rift-valley-explorer-safari",
    name: "The Rift Valley Explorer Safari",
    country: "Kenya",
    days: 8,
    duration: "8 days",
    tagline: "Alkaline lakes, cliffs, and the crack in the world.",
    level: "Photographers & professionals · Landscape safari",
    hero: HERO.landscapeA,
    intro:
      "Eight days through the Great Rift Valley — flamingos at Bogoria and Nakuru, cliffs at Elgeyo, boats on Naivasha.",
    route: ["Nairobi", "Lake Naivasha", "Lake Bogoria", "Lake Baringo", "Lake Nakuru"],
    highlights: [
      "Flamingo shores of Bogoria",
      "Boat safaris on Naivasha and Baringo",
      "Fish eagle sightings",
      "Rift escarpment views",
    ],
    lodges: [
      { name: "Loldia House", region: "Naivasha" },
      { name: "Lake Bogoria Spa Resort", region: "Bogoria" },
      { name: "The Cliff", region: "Nakuru" },
    ],
    dayList: [
      "Nairobi — arrival.",
      "Naivasha — Loldia House.",
      "Naivasha — boat safari; walks.",
      "Transfer to Bogoria — flamingos.",
      "Bogoria to Baringo — fish eagles.",
      "Transfer to Nakuru — The Cliff.",
      "Nakuru — flamingos and rhinos.",
      "Return — fly to Nairobi and depart.",
    ],
  }),
  j({
    slug: "bush-and-beach-safari",
    name: "Bush & Beach Safari",
    country: "Kenya",
    days: 9,
    duration: "9 days",
    tagline: "The plains, then the reef — Kenya in two halves.",
    level: "Photographers & professionals · Bush and beach",
    hero: HERO.tropicalBeach,
    intro:
      "Nine days composed in two halves — five days of predator drives on the Mara, four on the Indian Ocean coast.",
    route: ["Nairobi", "Masai Mara", "Diani Beach"],
    highlights: [
      "Big cats of the Mara",
      "Diani coral coast",
      "Reef snorkelling",
      "Dhow at sunset",
    ],
    lodges: [
      { name: "Angama Mara", region: "Masai Mara" },
      { name: "Alfajiri Villas", region: "Diani Beach" },
    ],
    dayList: [
      "Nairobi — arrival.",
      "Fly to Mara — Angama.",
      "Mara — full day.",
      "Mara — dawn balloon.",
      "Mara — private vehicle.",
      "Fly to Diani — Alfajiri.",
      "Diani — reef snorkel.",
      "Diani — dhow sunset.",
      "Depart — Mombasa to Nairobi, home.",
    ],
  }),
  j({
    slug: "journey-through-kenya",
    name: "Journey Through Kenya",
    country: "Kenya",
    days: 12,
    duration: "12 days",
    tagline: "The comprehensive Kenya — north to coast, twelve days.",
    level: "Photographers & professionals · Grand tour",
    hero: HERO.savannahMigration,
    intro:
      "Twelve days across all of Kenya's essential landscapes — the northern frontier, the central highlands, the Rift Valley lakes, the Mara, and the coast.",
    route: ["Nairobi", "Samburu", "Laikipia", "Nakuru", "Masai Mara", "Diani"],
    highlights: [
      "Samburu Special Five",
      "Laikipia conservancy",
      "Nakuru flamingos",
      "Mara predator dawns",
      "Diani coast",
    ],
    lodges: [
      { name: "Sasaab", region: "Samburu" },
      { name: "Ol Pejeta Bush Camp", region: "Laikipia" },
      { name: "The Cliff", region: "Nakuru" },
      { name: "Governors' Camp", region: "Masai Mara" },
      { name: "Alfajiri Villas", region: "Diani Beach" },
    ],
    dayList: [
      "Nairobi — arrival.",
      "Fly to Samburu — Sasaab.",
      "Samburu — Special Five.",
      "Fly to Laikipia — Ol Pejeta.",
      "Laikipia — rhino tracking.",
      "Fly to Nakuru — The Cliff.",
      "Nakuru — flamingos, rhinos.",
      "Fly to Mara — Governors'.",
      "Mara — full day.",
      "Mara — balloon and sundowners.",
      "Fly to Diani — Alfajiri.",
      "Depart.",
    ],
  }),
  j({
    slug: "explorer-safari-adventure-kenya",
    name: "Explorer Safari Adventure — Kenya",
    country: "Kenya",
    days: 8,
    duration: "8 days",
    tagline: "For the guests who want to walk, ride, and paddle.",
    level: "Photographers & professionals · Active safari",
    hero: HERO.lionCubs,
    intro:
      "Eight active days across Kenya — horseback in Laikipia, walking in Chyulu, dhow sailing on the coast. For guests who like the ground under their feet.",
    route: ["Nairobi", "Laikipia", "Chyulu Hills", "Diani"],
    highlights: [
      "Horseback safari on Laikipia",
      "Walking safaris in Chyulu",
      "Kilimanjaro views at dusk",
      "Coastal sailing",
    ],
    lodges: [
      { name: "Segera Retreat", region: "Laikipia" },
      { name: "Ol Donyo Lodge", region: "Chyulu Hills" },
      { name: "Alfajiri Villas", region: "Diani" },
    ],
    dayList: [
      "Nairobi — arrival.",
      "Fly to Laikipia — Segera.",
      "Laikipia — horseback safari.",
      "Fly to Chyulu — Ol Donyo.",
      "Chyulu — walking safari.",
      "Chyulu — Kilimanjaro sundowner.",
      "Fly to Diani — Alfajiri.",
      "Depart.",
    ],
  }),
  j({
    slug: "planet-explorer",
    name: "Planet Explorer",
    country: "Kenya",
    days: 14,
    duration: "14 days",
    tagline: "Two weeks. Every landscape Kenya keeps.",
    level: "Photographers & professionals · Two-week grand safari",
    hero: HERO.landscapeC,
    intro:
      "Fourteen days across every landscape Kenya keeps — deserts, forests, plains, lakes, and coast. The longest classic itinerary we design.",
    route: ["Nairobi", "Matthews Range", "Samburu", "Laikipia", "Nakuru", "Masai Mara", "Diani"],
    highlights: [
      "Northern frontier walking",
      "Samburu Special Five",
      "Laikipia conservancy",
      "Rift Valley lakes",
      "Mara predator dawns",
      "Coastal close",
    ],
    lodges: [
      { name: "Kitich Forest Camp", region: "Matthews Range" },
      { name: "Sasaab", region: "Samburu" },
      { name: "Segera Retreat", region: "Laikipia" },
      { name: "The Cliff", region: "Nakuru" },
      { name: "Angama Mara", region: "Masai Mara" },
      { name: "Alfajiri Villas", region: "Diani" },
    ],
    dayList: [
      "Nairobi — arrival.",
      "Fly to Matthews Range — Kitich.",
      "Matthews Range — walk.",
      "Transfer to Samburu — Sasaab.",
      "Samburu — Special Five.",
      "Fly to Laikipia — Segera.",
      "Laikipia — horseback.",
      "Fly to Nakuru — The Cliff.",
      "Nakuru — flamingos.",
      "Fly to Mara — Angama.",
      "Mara — full day.",
      "Mara — balloon.",
      "Fly to Diani — Alfajiri.",
      "Depart.",
    ],
  }),
  j({
    slug: "tanzania-explorer-classic",
    name: "Tanzania Explorer — Classic",
    country: "Tanzania",
    days: 8,
    duration: "8 days",
    tagline: "The classic Tanzania eight — crater, plains, cats.",
    level: "Photographers & professionals · Classic circuit",
    hero: HERO.zebraPortrait,
    intro:
      "Eight days across Tanzania's most iconic parks — Tarangire, Ngorongoro, and the Serengeti, each with three nights of proper access.",
    route: ["Arusha", "Tarangire", "Ngorongoro", "Serengeti"],
    highlights: [
      "Baobab country of Tarangire",
      "Ngorongoro Crater floor",
      "Serengeti big cats",
      "Bush breakfasts",
    ],
    lodges: [
      { name: "Chem Chem Lodge", region: "Tarangire" },
      { name: "The Highlands", region: "Ngorongoro" },
      { name: "Singita Faru Faru", region: "Serengeti" },
    ],
    dayList: [
      "Arusha — arrival.",
      "Fly to Tarangire — Chem Chem.",
      "Tarangire — elephants.",
      "Transfer to Ngorongoro — Highlands.",
      "Crater day.",
      "Fly to Serengeti — Faru Faru.",
      "Serengeti — big cats.",
      "Return — Arusha, depart.",
    ],
  }),
  j({
    slug: "tanzania-explorer-adventure",
    name: "Tanzania Explorer — Adventure",
    country: "Tanzania",
    days: 9,
    duration: "9 days",
    tagline: "The classic route, on foot and at close range.",
    level: "Photographers & professionals · Active safari",
    hero: HERO.landscapeD,
    intro:
      "Nine days across Tanzania designed around active experiences — walking safaris in Tarangire, the crater floor on foot where permitted, and Serengeti walking with senior guides.",
    route: ["Arusha", "Tarangire", "Ngorongoro", "Serengeti"],
    highlights: [
      "Walking safaris throughout",
      "Ngorongoro highlands hike",
      "Serengeti walking with rangers",
      "Bush overnight (optional)",
    ],
    lodges: [
      { name: "Oliver's Camp", region: "Tarangire" },
      { name: "Entamanu Ngorongoro", region: "Ngorongoro" },
      { name: "Sayari Camp", region: "Serengeti" },
    ],
    dayList: [
      "Arusha — arrival.",
      "Fly to Tarangire — Oliver's.",
      "Tarangire — walking safari.",
      "Transfer to Ngorongoro — Entamanu.",
      "Highlands hike.",
      "Crater day.",
      "Fly to Serengeti — Sayari.",
      "Serengeti — walking with rangers.",
      "Return — Arusha, depart.",
    ],
  }),
  j({
    slug: "tanzania-explorer-premium-luxury",
    name: "Tanzania Explorer — Premium Luxury",
    country: "Tanzania",
    days: 10,
    duration: "10 days",
    tagline: "The classic Tanzania route at the very top of the lodging scale.",
    level: "Photographers & professionals · Ultra-luxury",
    hero: HERO.amboseliSunset,
    intro:
      "Ten days across Tanzania staying only at the most extraordinary lodges — Singita, Four Seasons, &Beyond. Every detail curated at the very top of the scale.",
    route: ["Arusha", "Manyara", "Ngorongoro", "Central Serengeti", "Grumeti"],
    highlights: [
      "&Beyond Manyara Tree Lodge",
      "&Beyond Ngorongoro Crater Lodge",
      "Singita Grumeti Reserve",
      "Balloon and bush dining",
    ],
    lodges: [
      { name: "&Beyond Lake Manyara Tree Lodge", region: "Manyara" },
      { name: "&Beyond Ngorongoro Crater Lodge", region: "Ngorongoro" },
      { name: "Four Seasons Serengeti", region: "Central Serengeti" },
      { name: "Singita Sabora", region: "Grumeti" },
    ],
    dayList: [
      "Arusha — arrival.",
      "Fly to Manyara — Tree Lodge.",
      "Transfer to Ngorongoro — Crater Lodge.",
      "Crater day.",
      "Fly to Central Serengeti — Four Seasons.",
      "Serengeti — big cats.",
      "Serengeti — balloon.",
      "Fly to Grumeti — Singita Sabora.",
      "Grumeti — private reserve.",
      "Return — depart.",
    ],
  }),
  j({
    slug: "tanzania-ndoto-safari",
    name: "Tanzania Ndoto Safari",
    country: "Tanzania",
    days: 9,
    duration: "9 days",
    tagline: "Ndoto — the dream. A Tanzania journey composed for it.",
    level: "Photographers & professionals · Signature circuit",
    hero: HERO.landscapeE,
    intro:
      "Ndoto means 'dream'. Nine days threading the Tanzania many guests have imagined — crater, plains, migration, quiet camps.",
    route: ["Arusha", "Ngorongoro", "Central Serengeti", "Northern Serengeti"],
    highlights: [
      "Ngorongoro Crater floor",
      "Serengeti river crossings in season",
      "Balloon at dawn",
      "Small tented camps",
    ],
    lodges: [
      { name: "The Highlands", region: "Ngorongoro" },
      { name: "Sanctuary Kichakani", region: "Central Serengeti" },
      { name: "Lamai Serengeti", region: "Northern Serengeti" },
    ],
    dayList: [
      "Arusha — arrival.",
      "Transfer to Ngorongoro — Highlands.",
      "Crater day.",
      "Fly to Central Serengeti — Kichakani.",
      "Serengeti — big cats.",
      "Fly to Northern Serengeti — Lamai.",
      "Migration in season.",
      "Northern Serengeti — full day.",
      "Return — Arusha, depart.",
    ],
  }),
  j({
    slug: "tanzania-kumbu-kumbu-safari",
    name: "Tanzania Kumbu Kumbu Safari",
    country: "Tanzania",
    days: 12,
    duration: "12 days",
    tagline: "The keeper's Tanzania — twelve days, journal in hand.",
    level: "Photographers & professionals · Long-form",
    hero: HERO.landscapeB,
    intro:
      "Kumbu-kumbu means 'to remember'. Twelve days across Tanzania designed for guests keeping a journal — long enough to see slowly, short enough to stay sharp.",
    route: ["Arusha", "Tarangire", "Ngorongoro", "Central Serengeti", "Northern Serengeti", "Zanzibar"],
    highlights: [
      "Tarangire elephants",
      "Ngorongoro Crater",
      "Serengeti big cats and migration",
      "Zanzibar coast to close",
    ],
    lodges: [
      { name: "Chem Chem Lodge", region: "Tarangire" },
      { name: "Entamanu Ngorongoro", region: "Ngorongoro" },
      { name: "Sayari Camp", region: "Northern Serengeti" },
      { name: "The Residence Zanzibar", region: "Zanzibar" },
    ],
    dayList: [
      "Arusha — arrival.",
      "Fly to Tarangire — Chem Chem.",
      "Tarangire — walking.",
      "Ngorongoro — Entamanu.",
      "Crater day.",
      "Fly to Central Serengeti.",
      "Serengeti — big cats.",
      "Fly to Northern Serengeti — Sayari.",
      "Migration river crossings.",
      "Fly to Zanzibar — Residence.",
      "Zanzibar — Stone Town.",
      "Depart.",
    ],
  }),
  j({
    slug: "tanzania-origin",
    name: "Tanzania Origin",
    country: "Tanzania",
    days: 8,
    duration: "8 days",
    tagline: "Olduvai Gorge — the place where we began.",
    level: "Photographers & professionals · Anthropological",
    hero: HERO.landscapeF,
    intro:
      "Eight days composed around Olduvai Gorge and the Cradle of Humankind — a Tanzania journey with the palaeoanthropology at its centre.",
    route: ["Arusha", "Manyara", "Olduvai", "Ngorongoro", "Serengeti"],
    highlights: [
      "Olduvai Gorge with a resident researcher",
      "Ngorongoro Crater floor",
      "Serengeti big cats",
      "Manyara tree-climbing lions",
    ],
    lodges: [
      { name: "&Beyond Lake Manyara Tree Lodge", region: "Manyara" },
      { name: "The Highlands", region: "Ngorongoro" },
      { name: "Nomad Serengeti Safari Camp", region: "Serengeti" },
    ],
    dayList: [
      "Arusha — arrival.",
      "Manyara — Tree Lodge.",
      "Olduvai — private research visit.",
      "Ngorongoro — Highlands.",
      "Crater day.",
      "Fly to Serengeti — Nomad.",
      "Serengeti — big cats.",
      "Return — Arusha, depart.",
    ],
  }),
  j({
    slug: "the-birthing-of-the-wildebeeste",
    name: "The Birthing of the Wildebeest",
    country: "Tanzania",
    days: 10,
    duration: "10 days",
    tagline: "Calving season on the southern Serengeti — the greatest quiet spectacle.",
    level: "Photographers & professionals · Seasonal signature",
    hero: HERO.savannahMigration,
    intro:
      "January through March, half a million wildebeest calves are born on the southern Serengeti plains. Ten days composed to witness it — the greatest quiet spectacle in East Africa.",
    route: ["Arusha", "Ngorongoro", "Ndutu / Southern Serengeti", "Central Serengeti"],
    highlights: [
      "Calving season on the Ndutu plains",
      "Predator concentrations at their peak",
      "Ngorongoro Crater floor",
      "Green season photography",
    ],
    lodges: [
      { name: "The Highlands", region: "Ngorongoro" },
      { name: "&Beyond Serengeti Under Canvas", region: "Ndutu" },
      { name: "Singita Faru Faru", region: "Central Serengeti" },
    ],
    dayList: [
      "Arusha — arrival.",
      "Ngorongoro — Highlands.",
      "Crater day.",
      "Transfer to Ndutu — Under Canvas.",
      "Ndutu — calving on the plains.",
      "Ndutu — predator focus.",
      "Ndutu — full day.",
      "Fly to Central Serengeti — Faru Faru.",
      "Serengeti — big cats.",
      "Return — Arusha, depart.",
    ],
  }),
  j({
    slug: "uganda-gorillas-and-elephants",
    name: "Uganda — Gorillas and Elephants",
    country: "Uganda",
    days: 8,
    duration: "8 days",
    tagline: "Bwindi's gorillas and Queen Elizabeth's elephants.",
    level: "Photographers & professionals · Primates + big game",
    hero: HERO.gorilla,
    intro:
      "Eight days across Uganda's most storied parks — an hour with mountain gorillas in Bwindi, and elephant herds and tree-climbing lions in Queen Elizabeth.",
    route: ["Entebbe", "Bwindi", "Queen Elizabeth NP"],
    highlights: [
      "Mountain gorilla trekking",
      "Tree-climbing lions of Ishasha",
      "Kazinga Channel boat safari",
      "Batwa cultural exchange",
    ],
    lodges: [
      { name: "Clouds Mountain Gorilla Lodge", region: "Bwindi" },
      { name: "Kyambura Gorge Lodge", region: "Queen Elizabeth NP" },
    ],
    dayList: [
      "Entebbe — arrival.",
      "Fly to Bwindi — Clouds.",
      "Gorilla trek.",
      "Second trek option.",
      "Transfer to Queen Elizabeth — Kyambura.",
      "Ishasha — tree-climbing lions.",
      "Kazinga Channel boat.",
      "Depart — return to Entebbe.",
    ],
  }),
];

export const findItinerary = (slug) => itineraries.find((i) => i.slug === slug);

// Backwards-compat: JourneyDetail expects `days` as an array of objects with title/detail.
// Derive it from dayList (a compact string form) if present.
itineraries.forEach((i) => {
  if (!i.days || typeof i.days === "number") {
    i.duration = i.duration || `${i.days} days`;
  }
  if (i.dayList && !Array.isArray(i.days)) {
    // Preserve numeric day count on a separate property
    i.dayCount = typeof i.days === "number" ? i.days : i.dayList.length;
    i.days = i.dayList.map((line, idx) => {
      const [title, ...rest] = line.split(" — ");
      return {
        day: idx + 1,
        title: title.trim(),
        detail: rest.join(" — ").trim() || title.trim(),
      };
    });
  } else if (typeof i.days === "number") {
    // no dayList — synthesize placeholder
    i.dayCount = i.days;
    i.days = Array.from({ length: i.days }, (_, idx) => ({
      day: idx + 1,
      title: `Day ${idx + 1}`,
      detail: "",
    }));
  }
});

// Category getter — used by the Journeys listing to group items.
export const categoryFor = (it) => (it.dayCount <= 7 ? "families" : "professionals");
